/**
 * Labeled numeric min/max range field: two digit-only text inputs joined by
 * "to". Values are controlled via min/max plus optional setMin/setMax setters
 * (an input is disabled when its setter is absent or `disabled` is set); empty
 * input maps to null. Used for reward ranges in BountyCreateEdit and
 * BountyDisplay.
 */
import React from "react";

type RangeFieldProps = {
    label: string;
    min: number | null;
    max: number | null;
    setMin?: React.Dispatch<React.SetStateAction<number | null>> | null;
    setMax?: React.Dispatch<React.SetStateAction<number | null>> | null;
    className?: string;
    disabled?: boolean;
};

const RangeField: React.FC<RangeFieldProps> = ({
    label,
    min,
    max,
    setMin,
    setMax,
    className,
    disabled,
}) => {
    const inputClass = `w-24 h-10 px-4 py-2 border rounded-lg focus:border-black focus:outline-none tracking-wider ${
        disabled ? "disabled:bg-white" : ""
    } text-sm`;

    return (
        <div
            key={"range-field"}
            className={`w-full flex flex-col font-porsui text-slate-950  ${className}`}
        >
            <label
                htmlFor={"range-field"}
                className="block text-sm font-medium mb-1 tracking-widest"
            >
                {label}
            </label>
            <div className="flex flex-row justify-between items-center space-x-4">
                <input
                    id="min"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={min === null ? "" : String(min)}
                    className={`${inputClass} flex-1`}
                    disabled={disabled || !setMin}
                    onChange={(e) => {
                        const digitsOnly = (e.target.value || "").replace(/\D/g, "");
                        setMin?.(digitsOnly === "" ? null : Number(digitsOnly));
                    }}
                />
                <span className="flex-shrink-0">to</span>
                <input
                    id="max"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={max === null ? "" : String(max)}
                    className={`${inputClass} flex-1`}
                    disabled={disabled || !setMax}
                    onChange={(e) => {
                        const digitsOnly = (e.target.value || "").replace(/\D/g, "");
                        setMax?.(digitsOnly === "" ? null : Number(digitsOnly));
                    }}
                />
            </div>
        </div>
    );
};

export default RangeField;
